import { useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  AppShell,
  Footer,
  Sidebar,
  Topbar,
  UserMenuPopover,
  type SidebarItem,
} from "@act-group/act-ui-library";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import HomeIcon from "@mui/icons-material/Home";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import SettingsIcon from "@mui/icons-material/Settings";
import ReportGmailerrorredIcon from "@mui/icons-material/ReportGmailerrorred";
import NotificationsIcon from "@mui/icons-material/Notifications";
import ActLogo from "../components/ActLogo/ActLogo";
import { useThemeContext } from "../contexts/ThemeContext";
import { useUserContext } from "../contexts/UserContext";
import { canReturnToPortal, getApplicationEntry, KYC_APPLICATION_NAME } from "../types/auth";
import { AUTH_BASE_URL, AUTH_LOGOUT_URL } from "../constants/auth";
import { cookiesCleanup } from "../utils/authStorage";

// Menu icons match the Demo/SI Console sidebar: 2.5rem white circular chip
// with a black glyph, in both light and dark mode.
const NAV_ICON_SX = {
  fontSize: "2.5rem",
  p: "0.25rem",
  borderRadius: "50%",
  bgcolor: "#ffffff",
  color: "#000000",
} as const;

// The notifications bell is hidden for now (no notifications feature to open
// yet). The button itself is kept behind this flag rather than deleted — flip
// it back to true once there's something for it to show. The topbar's actions
// row is a gap-based Stack, so hiding it just closes the gap; the remaining
// icons stay right-aligned.
const SHOW_NOTIFICATIONS: boolean = false;

const NAV_ITEMS: SidebarItem[] = [
  { id: "/", label: "Dashboard", icon: <HomeIcon sx={NAV_ICON_SX} />, href: "/" },
  {
    id: "/kyc",
    label: "Pending",
    // Same ReportGmailerrorred glyph as the SI Console's reports entry
    icon: <ReportGmailerrorredIcon sx={NAV_ICON_SX} />,
    href: "/kyc",
  },
  // Same filled PeopleAlt glyph as the SI Console sidebar
  { id: "/users", label: "Users", icon: <PeopleAltIcon sx={NAV_ICON_SX} />, href: "/users" },
  {
    id: "/settings",
    label: "Settings",
    // Same filled Settings glyph as the SI Console sidebar
    icon: <SettingsIcon sx={NAV_ICON_SX} />,
    href: "/settings",
  },
];

function MainLayout() {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [sidebarOpen, setSidebarOpen] = useState(!isSmallScreen);
  const navigate = useNavigate();
  const location = useLocation();
  const { darkMode } = useThemeContext();
  const { userInfo } = useUserContext();
  const { t } = useTranslation();
  const mode = darkMode ? "dark" : "light";

  // Translate nav labels at render (ids/hrefs stay stable) so the sidebar
  // re-labels on language change.
  const navItems = useMemo<SidebarItem[]>(
    () => NAV_ITEMS.map((item) => ({ ...item, label: t(item.label as string) })),
    [t]
  );

  useEffect(() => {
    setSidebarOpen(!isSmallScreen);
  }, [isSmallScreen]);

  const activeItemId = useMemo(() => {
    const match = NAV_ITEMS.find(
      (item) =>
        item.href === location.pathname ||
        (item.href !== "/" && location.pathname.startsWith(`${item.href}/`))
    );
    return match?.id;
  }, [location.pathname]);

  const kycApplication = getApplicationEntry(userInfo, KYC_APPLICATION_NAME);
  const userRole = kycApplication?.userTypeName ?? "Guest";
  const userName = userInfo?.name ?? "Guest";
  const productSelectorEnabled = canReturnToPortal(userInfo);

  const handleSignOut = () => {
    cookiesCleanup();
    window.location.href = AUTH_LOGOUT_URL;
  };

  // Clicking the ACT logo returns to the portal's product selector, matching the
  // ACT SI Console's drawer logo (onLogoClick → AUTH_BASE_URL).
  const handleProductSelector = () => {
    window.location.href = AUTH_BASE_URL;
  };

  return (
    <AppShell
      footer={
        // The Demo/SI Console footer uses its own surface colors (#d8d8d8
        // light / #1c1c1c dark) rather than the library default (action.hover).
        <Box sx={{ "& > footer": { bgcolor: mode === "dark" ? "#1c1c1c" : "#d8d8d8" } }}>
          <Footer
            logo={<ActLogo height={40} />}
            copyright={t("Copyright", { year: new Date().getFullYear() })}
            version={`${t("Version")} 0.1.0`}
            links={[
              { id: "terms", label: t("Terms of Use"), href: "#" },
              { id: "privacy", label: t("Privacy Policy"), href: "#" },
              { id: "cookies", label: t("Cookies Policy"), href: "#" },
              { id: "contact", label: t("Contact Us"), href: "#" },
            ]}
          />
        </Box>
      }
      topbar={
        <Topbar
          title={t("ID Verification")}
          sidebarOpen={sidebarOpen}
          // Demo/SI Console pattern: while the sidebar is collapsed the topbar
          // spans the full width (covering the rail's header) and hosts the
          // hamburger; the rail's icons start below it.
          fullWidthWhenCollapsed
          leading={
            !sidebarOpen && (
              <IconButton
                edge="start"
                aria-label={t("Open sidebar")}
                onClick={() => setSidebarOpen(true)}
                sx={{ mr: 3 }}
              >
                <MenuIcon
                  fontSize="medium"
                  sx={{ color: mode === "dark" ? "#ececec" : "#676767" }}
                />
              </IconButton>
            )
          }
          actions={
            // Same icon spacing as the SI Console topbar
            <Stack
              direction="row"
              alignItems="center"
              sx={{ gap: isSmallScreen ? "0.2rem" : "1.2rem" }}
            >
              {/* Reports + Notifications — same treatment as the SI Console
                  topbar: filled 1.8rem glyphs inside 2.5rem circular buttons
                  (white in light mode, near-black in dark mode). */}
              {/* Goes to the Pending queue, mirroring the SI Console's reports button. */}
              <IconButton
                aria-label={t("Pending")}
                onClick={() => navigate("/kyc")}
                sx={{
                  width: "2.8rem",
                  height: "2.8rem",
                  backgroundColor: mode === "dark" ? "#121212" : "#ffffff",
                  "&:hover": {
                    backgroundColor: mode === "dark" ? "#404040" : "#ddd",
                  },
                }}
              >
                <ReportGmailerrorredIcon
                  sx={{
                    fontSize: "1.8rem",
                    color: mode === "dark" ? "#ffffff" : "#121212",
                  }}
                />
              </IconButton>
              {SHOW_NOTIFICATIONS && (
                <IconButton
                  aria-label={t("Notifications")}
                  sx={{
                    width: "2.8rem",
                    height: "2.8rem",
                    backgroundColor: mode === "dark" ? "#121212" : "#ffffff",
                    "&:hover": {
                      backgroundColor: mode === "dark" ? "#404040" : "#dddddd",
                    },
                  }}
                >
                  <NotificationsIcon
                    sx={{
                      fontSize: "1.8rem",
                      color: mode === "dark" ? "#ffffff" : "#121212",
                    }}
                  />
                </IconButton>
              )}
              {/* Brand-styled avatar trigger (matches the Demo/SI Console):
                  white circle (dark grey in dark mode), person icon in the
                  text color, green status dot at the top right. */}
              <UserMenuPopover
                clientName="ACT"
                userName={userName}
                userRole={userRole}
                roleLabel={userRole}
                onSignOut={handleSignOut}
                signOutLabel={t("sign out")}
                closeLabel={t("close")}
                avatarSx={{
                  // bgcolor: mode === "dark" ? "#121212" : "common.white",
                  backgroundColor: mode === "dark" ? "#121212" : "#ffffff",
                  "&:hover": {
                    backgroundColor: mode === "dark" ? "#404040" : "#dddddd",
                  },
                  color: "text.primary",
                  // borderColor: "divider",
                  width: "2.8rem",
                  height: "2.8rem",
                  // Bigger person icon, same icon-to-circle ratio as the SI Console
                  "& .MuiSvgIcon-root": { fontSize: 40 },
                }}
                statusDotSx={
                  {
                    // Sit on the person icon's head (top right), like the SI
                    // Console, rather than on the avatar's outline.
                    // top: 7,
                    // right: 9,
                    // bottom: "auto",
                    // width: 8,
                    // height: 8,
                    // border: "none",
                    // bgcolor: "status.none",
                  }
                }
              />
            </Stack>
          }
        />
      }
      sidebar={
        <Sidebar
          items={navItems}
          open={sidebarOpen}
          activeItemId={activeItemId}
          onNavigate={(item) => item.href && navigate(item.href)}
          header={(expanded) =>
            expanded ? (
              <>
                {/* Inert for a user with a single application: there is nothing
                    to select between, and the portal would log them out on the
                    way back. ButtonBase's own disabled styles drop the pointer
                    cursor and hover, leaving the logo looking unchanged. */}
                <ButtonBase
                  aria-label={t("Product selector")}
                  disabled={!productSelectorEnabled}
                  onClick={handleProductSelector}
                  sx={{ pl: 1, display: "flex", alignItems: "center", borderRadius: 1 }}
                >
                  {/* Same 2rem logo size as the Demo/SI Console drawer header */}
                  <ActLogo height={32} />
                </ButtonBase>
                <IconButton
                  size="small"
                  aria-label={t("Close sidebar")}
                  onClick={() => setSidebarOpen(false)}
                >
                  <CloseIcon
                    fontSize="medium"
                    sx={{ color: mode === "dark" ? "#ececec" : "#676767" }}
                  />
                </IconButton>
              </>
            ) : (
              // Collapsed: the full-width topbar covers this header, so it
              // only acts as a spacer keeping the rail icons below the topbar
              <Box />
            )
          }
        />
      }
    >
      <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 }, width: "100%" }}>
        <Outlet />
      </Box>
    </AppShell>
  );
}

export default MainLayout;
